import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    FaArchive, FaBookOpen, FaEdit, FaPlus, FaRegStar, FaStar,
    FaSearch, FaFileAlt, FaExternalLinkAlt, FaEye, FaDownload, FaSlidersH, FaChartBar,
} from "react-icons/fa";
import { MANUALS_CONSTANTS } from "../_MODULES/_API";
import { ManualFormModal } from "../_UI/ManualFormModal";
import { PageHeader } from "../../../../../_CORE/APP/Templates/_UI/PageHeader/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { FieldDate, FieldInput, FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { ViewLayout } from "../../../../_UI/ViewLayout";
import { onApiAction } from "../../../../_UI/onApiAction";
import { onSilentPatch } from "../../../../_UI/onSilentPatch";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { REACT_HOT_TOAST_ERROR } from "../../../../../_CORE/HELPERS/reactHotToast.helper";
import { isDuplicateRecord } from "../../../../_UI/recordUniqueness";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { AdvancedFilterPanel, onDateMatches } from "../../../../_UI/AdvancedFilterPanel";
import { StatisticsModal } from "../../../../_UI/StatisticsModal";
import { downloadMarkdownTreeZip, downloadPdfTree } from "../../../../_UI/DocumentExport";
import "../_UI/_Styles/Manuals.css";

const CATEGORY_LABEL = {
    GENERAL: "General", LANGUAGE: "Lenguaje", FRAMEWORK: "Framework", BACKEND: "Backend",
    FRONTEND: "Frontend", DATABASE: "Base de datos", DEVOPS: "DevOps", CLOUD: "Cloud",
    TOOL: "Herramienta", OTHER: "Otro",
};

const onDate = (value) => value ? new Date(value).toLocaleDateString("es-AR", {
    day: "2-digit", month: "short", year: "numeric",
}) : "-";

const onSortPages = (pages = []) => [...pages].sort((a, b) =>
    Number(a.position || 0) - Number(b.position || 0)
    || String(a.title || "").localeCompare(String(b.title || ""), "es"),
);

const onBuildTree = (pages = []) => {
    const map = new Map();
    onSortPages(pages).forEach((page) => map.set(page.id, { ...page, children: [] }));
    const roots = [];
    map.forEach((page) => {
        const parent = page.parentId ? map.get(page.parentId) : null;
        if (parent && parent.id !== page.id) parent.children.push(page);
        else roots.push(page);
    });
    const deepSort = (items) => {
        items.sort((a, b) => Number(a.position || 0) - Number(b.position || 0));
        items.forEach((item) => deepSort(item.children));
        return items;
    };
    return deepSort(roots);
};

export const ManualsViewMain = () => {
    const navigate = useNavigate();
    const [useIsOpen, setIsOpen] = useState(false);
    const [useEditing, setEditing] = useState(null);
    const [useSearch, setSearch] = useState("");
    const [useExportingId, setExportingId] = useState("");
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [statisticsOpen, setStatisticsOpen] = useState(false);
    const [advanced, setAdvanced] = useState({ favorite: "ALL", category: "ALL", technology: "", dateField: "UPDATED", dateRange: [null, null], dateExact: null });

    const {
        apiGet: onGetManuals,
        useApiData: MANUALS,
        useApiLoading: IS_LOADING,
        mutateLocal,
    } = useApiQuery({ minTimeQuery: 250, enablePagination: false, startLoading: true });

    const { apiGet: onGetManualForExport } = useApiQuery({
        minTimeQuery: 120, enablePagination: false, startLoading: false,
    });

    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 220,
    });

    const onLoad = () => onGetManuals({ endpoint: MANUALS_CONSTANTS.LIST });
    useEffect(() => { onLoad(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onSave = async (values) => {
        const isEdit = !!useEditing?.id;
        const previous = isEdit ? { ...useEditing } : null;
        if (isDuplicateRecord({ rows: Array.isArray(MANUALS) ? MANUALS : [], value: values.title, currentId: useEditing?.id, getValue: (row) => row?.title })) {
            REACT_HOT_TOAST_ERROR("Ya existe un manual con ese título");
            return false;
        }

        // Local-first: si editamos, la biblioteca cambia antes de esperar al servidor.
        if (isEdit) mutateLocal({ id: useEditing.id, patch: values });

        const response = await actionMutate({
            method: isEdit ? "PATCH" : "POST",
            endpoint: isEdit ? MANUALS_CONSTANTS.UPDATE : MANUALS_CONSTANTS.CREATE,
            id: isEdit ? useEditing.id : null,
            data: values,
            successMessage: isEdit ? "Manual actualizado" : "Manual creado",
        });

        if (!response.success) {
            if (isEdit && previous) mutateLocal({ id: previous.id, patch: previous });
            return false;
        }

        const responseData = response?.data?.data || response?.data || null;
        const createdId = !isEdit ? (responseData?.id || response?.id) : null;

        if (isEdit && responseData?.id) mutateLocal({ id: useEditing.id, patch: responseData });
        if (!isEdit && responseData?.id) mutateLocal({ insert: responseData });

        setEditing(null);
        if (createdId) window.setTimeout(() => navigate(`/manuals/${createdId}?mode=edit`), 0);
        return true;
    };

    const onArchive = async (manual) => {
        const ok = await confirm.danger(
            "Archivar manual",
            `Se archiva "${manual.name}" con todas sus paginas. Lo podes restaurar o eliminar definitivamente desde Archivados.`,
        );
        if (!ok) return;

        // Local-first: desaparece ahora; el backend persiste en segundo plano.
        mutateLocal({ id: manual.id, remove: true });

        const response = await onApiAction({
            method: "PATCH", endpoint: MANUALS_CONSTANTS.ARCHIVE_ONE,
            pathParams: { id: manual.id }, successMessage: "Manual archivado",
        });

        if (!response.success) mutateLocal({ insert: manual });
    };

    const onToggleFavorite = async (manual) => {
        const previous = !!manual.isFavorite;
        const next = !previous;
        mutateLocal({ id: manual.id, patch: { isFavorite: next } });

        const response = await onSilentPatch({
            endpoint: MANUALS_CONSTANTS.UPDATE,
            id: manual.id,
            data: { isFavorite: next },
            errorMessage: "No se pudo guardar el destacado del manual.",
        });

        if (!response.success) mutateLocal({ id: manual.id, patch: { isFavorite: previous } });
    };

    const onExportManual = async (manual, format) => {
        if (!manual?.id || useExportingId) return;
        setExportingId(manual.id);
        try {
            const response = await onGetManualForExport({
                endpoint: MANUALS_CONSTANTS.DETAIL,
                isDetail: true,
                config: { pathParams: { id: manual.id } },
            });
            if (!response?.success || !response?.data) return;
            const detail = response.data;
            const options = {
                title: detail.name || manual.name || "Manual",
                description: detail.description || manual.description || "",
                tree: onBuildTree(detail.pages || []),
            };
            if (format === "pdf") downloadPdfTree(options);
            if (format === "markdown") downloadMarkdownTreeZip(options);
            mutateLocal({ id: manual.id, patch: { pageCount: Number(detail.pageCount ?? detail.pages?.length ?? manual.pageCount ?? 0) } });
        } finally {
            setExportingId("");
        }
    };

    const filtered = useMemo(() => {
        const rows = Array.isArray(MANUALS) ? MANUALS : [];
        return rows.filter((manual) => {
            if (advanced.favorite !== "ALL" && (!!manual.isFavorite !== (advanced.favorite === "YES"))) return false;
            if (advanced.category !== "ALL" && String(manual.category || "") !== String(advanced.category)) return false;
            if (String(advanced.technology || "").trim() && !onMatchesSearch(advanced.technology, [manual.technology, manual.version])) return false;
            const dateValue = advanced.dateField === "CREATED" ? manual.createdAt : manual.updatedAt;
            if (!onDateMatches(dateValue, advanced.dateRange, advanced.dateExact)) return false;
            return onMatchesSearch(useSearch, [manual.name, manual.description, manual.technology, manual.version, CATEGORY_LABEL[manual.category]]);
        });
    }, [MANUALS, useSearch, advanced]);
    const advancedCount = [advanced.favorite !== "ALL", advanced.category !== "ALL", String(advanced.technology || "").trim(), advanced.dateRange?.[0] || advanced.dateRange?.[1], advanced.dateExact].filter(Boolean).length;
    const clearAdvanced = () => setAdvanced({ favorite: "ALL", category: "ALL", technology: "", dateField: "UPDATED", dateRange: [null, null], dateExact: null });

    const totalPages = useMemo(() => (Array.isArray(MANUALS) ? MANUALS : [])
        .reduce((acc, item) => acc + Number(item.pageCount || 0), 0), [MANUALS]);

    const exportColumns = [
        { key: "name", label: "Manual" },
        { key: "category", label: "Categoría" },
        { key: "technology", label: "Tecnología" },
        { key: "version", label: "Versión" },
        { key: "pages", label: "Páginas" },
        { key: "favorite", label: "Destacado" },
        { key: "updatedAt", label: "Actualizado" },
    ];
    const exportRows = filtered.map((manual) => ({
        name: manual.name || "",
        category: CATEGORY_LABEL[manual.category] || manual.category || "General",
        technology: manual.technology || "",
        version: manual.version || "",
        pages: Number(manual.pageCount || 0),
        favorite: manual.isFavorite ? "Sí" : "No",
        updatedAt: onDate(manual.updatedAt),
    }));

    const toolbar = <ViewToolbar
        filters={<>
            <SearchFilter value={useSearch} onChange={setSearch} total={Array.isArray(MANUALS) ? MANUALS.length : 0} matches={filtered.length} placeholder="Ej.: React, NestJS, Docker o API" />
            <AppButton variant={advancedOpen ? "soft" : "outline"} tone="neutral" icon={<FaSlidersH />} onClick={() => setAdvancedOpen((current) => !current)}>{advancedCount ? `Avanzado (${advancedCount})` : "Avanzado"}</AppButton>
        </>}
        expandedContent={<AdvancedFilterPanel isOpen={advancedOpen} activeCount={advancedCount} onClear={clearAdvanced}>
            <FieldSelect id="manuals_adv_favorite" label="Destacado" value={advanced.favorite} onChange={(event) => setAdvanced((current) => ({ ...current, favorite: event.target.value }))} options={[{ value: "ALL", label: "Todos" }, { value: "YES", label: "Destacados" }, { value: "NO", label: "No destacados" }]} />
            <FieldSelect id="manuals_adv_category" label="Categoría" value={advanced.category} onChange={(event) => setAdvanced((current) => ({ ...current, category: event.target.value }))} options={[{ value: "ALL", label: "Todas" }, ...Object.entries(CATEGORY_LABEL).map(([value,label]) => ({ value,label }))]} isSearchable />
            <FieldInput id="manuals_adv_tech" label="Tecnología / versión" placeholder="Ej.: React 19, PHP 8.3 o Docker" value={advanced.technology} onChange={(event) => setAdvanced((current) => ({ ...current, technology: event.target.value }))} />
            <FieldSelect id="manuals_adv_date" label="Fecha" value={advanced.dateField} onChange={(event) => setAdvanced((current) => ({ ...current, dateField: event.target.value }))} options={[{ value: "UPDATED", label: "Última actualización" }, { value: "CREATED", label: "Creación" }]} />
            <FieldDate id="manuals_adv_from" label="Desde" value={advanced.dateRange?.[0] || null} onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [date || null, current.dateRange?.[1] || null], dateExact: null }))} isClearable />
            <FieldDate id="manuals_adv_to" label="Hasta" value={advanced.dateRange?.[1] || null} onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [current.dateRange?.[0] || null, date || null], dateExact: null }))} isClearable />
            <FieldDate id="manuals_adv_exact" label="Fecha exacta" value={advanced.dateExact} onChange={(date) => setAdvanced((current) => ({ ...current, dateExact: date || null, dateRange: date ? [null, null] : current.dateRange }))} isClearable />
        </AdvancedFilterPanel>}
        exportColumns={exportColumns}
        exportRows={exportRows}
        exportName="manuales"
        total={filtered.length}
        totalSingular="manual"
        totalPlural="manuales"
    />;

    return (
        <ViewLayout header={
            <PageHeader
                title="Manuales"
                icon={<FaBookOpen />}
                description="Tu biblioteca tecnica personal. Cada manual funciona como un espacio de documentacion con paginas, subpaginas y Markdown."
                actions={<>
                    <AppButton variant="outline" tone="neutral" icon={<FaChartBar />} isIconOnly title="Estadísticas" aria-label="Estadísticas" onClick={() => setStatisticsOpen(true)} />
                    <AppButton icon={<FaPlus />} onClick={() => { setEditing(null); setIsOpen(true); }}>Nuevo manual</AppButton>
                </>}
            />
        } toolbar={toolbar}>
            <div className="LStudio__manual_library_list" data-loading={IS_LOADING}>
                    {IS_LOADING && (
                        <div className="LStudio__manual_library_empty">Cargando biblioteca...</div>
                    )}

                    {!IS_LOADING && filtered.map((manual) => (
                        <div
                            key={manual.id}
                            className="LStudio__manual_library_row"
                            role="button"
                            tabIndex={0}
                            onClick={() => navigate(`/manuals/${manual.id}?mode=read`)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") navigate(`/manuals/${manual.id}?mode=read`);
                            }}
                        >
                            <div className="LStudio__manual_library_book" style={{ "--manual-color": manual.color || "#e83a52" }}>
                                <FaBookOpen />
                            </div>

                            <div className="LStudio__manual_library_main">
                                <div className="LStudio__manual_library_titleline">
                                    <strong>{manual.name}</strong>
                                    {manual.isFavorite && <FaStar className="LStudio__manual_library_favorite" />}
                                </div>
                                <p>{manual.description || "Sin descripcion. Entra y empeza a documentar."}</p>
                                <div className="LStudio__manual_library_meta">
                                    {manual.technology && <span>{manual.technology}</span>}
                                    {manual.version && <span>v{manual.version}</span>}
                                    <span>{CATEGORY_LABEL[manual.category] || manual.category || "General"}</span>
                                    <span><FaFileAlt /> {manual.pageCount || 0} paginas</span>
                                    <span>Actualizado {onDate(manual.updatedAt)}</span>
                                </div>
                            </div>

                            <div className="LStudio__manual_library_links" onClick={(event) => event.stopPropagation()}>
                                <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly
                                    icon={<FaEye />} aria-label="Ver manual en modo lectura"
                                    onClick={() => navigate(`/manuals/${manual.id}?mode=read`)} />
                                <CardActionMenu
                                    title="Descargar manual"
                                    ariaLabel={`Opciones de descarga de ${manual.name || "manual"}`}
                                    items={[
                                        {
                                            key: "download-pdf",
                                            label: useExportingId === manual.id ? "Preparando PDF..." : "Descargar PDF completo",
                                            icon: <FaFileAlt />,
                                            isDisabled: !!useExportingId,
                                            onClick: () => onExportManual(manual, "pdf"),
                                        },
                                        {
                                            key: "download-markdown",
                                            label: useExportingId === manual.id ? "Preparando Markdown..." : "Descargar Markdown por carpetas (.zip)",
                                            icon: <FaDownload />,
                                            isDisabled: !!useExportingId,
                                            onClick: () => onExportManual(manual, "markdown"),
                                        },
                                    ]}
                                />
                                {manual.officialDocsUrl && (
                                    <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly
                                        icon={<FaExternalLinkAlt />} aria-label="Documentacion oficial"
                                        href={manual.officialDocsUrl} />
                                )}
                                <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly
                                    icon={manual.isFavorite ? <FaStar /> : <FaRegStar />}
                                    aria-label={manual.isFavorite ? "Quitar favorito" : "Marcar favorito"}
                                    onClick={() => onToggleFavorite(manual)} />
                                <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly
                                    icon={<FaEdit />} aria-label="Editar contenido del manual"
                                    onClick={() => navigate(`/manuals/${manual.id}?mode=edit`)} />
                                <AppButton size="sm" variant="ghost" tone="danger" isIconOnly
                                    icon={<FaArchive />} aria-label="Archivar manual"
                                    onClick={() => onArchive(manual)} />
                            </div>
                        </div>
                    ))}

                    {!IS_LOADING && filtered.length === 0 && (
                        <div className="LStudio__manual_library_empty">
                            <FaBookOpen />
                            <strong>{useSearch || advancedCount ? "No encontramos manuales" : "Todavia no hay manuales"}</strong>
                            <span>{useSearch || advancedCount ? "Probá con otra búsqueda o limpiá los filtros." : "Creá uno y usalo como tu GitBook personal."}</span>
                            {useSearch || advancedCount ? (
                                <AppButton variant="outline" tone="neutral" onClick={() => { setSearch(""); clearAdvanced(); }}>Limpiar filtros</AppButton>
                            ) : (
                                <AppButton icon={<FaPlus />} onClick={() => setIsOpen(true)}>Crear primer manual</AppButton>
                            )}
                        </div>
                    )}
            </div>

            <StatisticsModal isOpen={statisticsOpen} onClose={() => setStatisticsOpen(false)} title="Estadísticas · Manuales" description="Resumen general de tu biblioteca de manuales." metrics={[
                { key: "manuals", label: "Manuales", value: Array.isArray(MANUALS) ? MANUALS.length : 0, icon: <FaBookOpen />, tone: "accent", isLoading: IS_LOADING },
                { key: "pages", label: "Páginas", value: totalPages, icon: <FaFileAlt />, tone: "neutral", isLoading: IS_LOADING },
                { key: "favorites", label: "Destacados", value: (Array.isArray(MANUALS) ? MANUALS : []).filter((manual) => manual.isFavorite).length, icon: <FaStar />, tone: "ok", isLoading: IS_LOADING },
            ]} />

            <ManualFormModal
                isOpen={useIsOpen}
                onClose={() => { setIsOpen(false); setEditing(null); }}
                manual={useEditing}
                onSave={onSave}
                isLoading={useActionApiIsLoading}
            />
        </ViewLayout>
    );
};
